import React, { useState, useEffect, useCallback } from "react";
import {
  Container, Card, Table, Form, Row, Col,
  Spinner, Alert, Badge, Button, Modal,
} from "react-bootstrap";
import api, { BASE_URL } from "../../services/api";
import "./Dashboard.css";

const jenisPelatihanLabel = {
  PPH01: "Penyelia Halal UMK Self Declare",
  PPH02: "Penyelia Halal UMK Reguler",
  PPH03: "Penyelia Halal Reguler",
};

const STATUS_OPTIONS = [
  { value: "",                 label: "— Semua —" },
  { value: "PENDAFTARAN",      label: "Pendaftaran" },
  { value: "DITOLAK",         label: "Ditolak" },
  { value: "PESERTA PELATIHAN", label: "Peserta Pelatihan" },
  { value: "LULUS",            label: "Lulus" },
  { value: "TIDAK LULUS",      label: "Tidak Lulus" },
  { value: "TERBIT SERTIFIKAT", label: "Terbit Sertifikat" },
];

const STATUS_BADGE = {
  "PENDAFTARAN":       { bg: "warning",   text: "dark" },
  "DITOLAK":           { bg: "danger",    text: undefined },
  "PESERTA PELATIHAN": { bg: "primary",   text: undefined },
  "LULUS":             { bg: "success",   text: undefined },
  "TIDAK LULUS":       { bg: "secondary", text: undefined },
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("id-ID", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const StatusBadge = ({ status, fileSertifikat }) => {
  if (status === "LULUS" && fileSertifikat) {
    return <Badge bg="info">TERBIT SERTIFIKAT</Badge>;
  }
  const cfg = STATUS_BADGE[status] || { bg: "light", text: "dark" };
  return (
    <Badge bg={cfg.bg} text={cfg.text}>
      {status || "-"}
    </Badge>
  );
};

const PesertaInquiry = () => {
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");

  const [jenisList, setJenisList]             = useState([]);
  const [angkatanList, setAngkatanList]       = useState([]);
  const [filterStatus, setFilterStatus]       = useState("");
  const [filterJenis, setFilterJenis]         = useState("");
  const [filterAngkatan, setFilterAngkatan]   = useState("");
  const [searchText, setSearchText]           = useState("");

  // Hapus
  const [showHapusModal, setShowHapusModal] = useState(false);
  const [targetHapus, setTargetHapus]       = useState(null);
  const [hapusLoading, setHapusLoading]     = useState(false);
  const [hapusError, setHapusError]         = useState("");

  useEffect(() => { fetchFilterOptions(); }, []);
  useEffect(() => { fetchData(); }, [filterStatus, filterJenis, filterAngkatan]); // eslint-disable-line

  const fetchFilterOptions = async () => {
    try {
      const res = await api.get("/pendaftaran-penyelia/filter-options");
      if (res.data.success) {
        setJenisList(res.data.jenis_pelatihan || []);
        setAngkatanList(res.data.angkatan || []);
      }
    } catch (err) {
      console.error("Error fetching filter options:", err);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = {};
      if (filterStatus)   params.status          = filterStatus;
      if (filterJenis)    params.jenis_pelatihan  = filterJenis;
      if (filterAngkatan) params.angkatan         = filterAngkatan;
      const res = await api.get("/pendaftaran-penyelia/inquiry", { params });
      if (res.data.success) setData(res.data.data);
      else setError("Gagal memuat data.");
    } catch (err) {
      setError(err.response?.data?.message || "Gagal memuat data dari server.");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterJenis, filterAngkatan]);

  const filteredData = data.filter((row) => {
    if (!searchText) return true;
    const term = searchText.toLowerCase();
    return (
      row.nama?.toLowerCase().includes(term) ||
      row.email?.toLowerCase().includes(term) ||
      row.no_hp?.toLowerCase().includes(term) ||
      row.nik?.toLowerCase().includes(term) ||
      row.no_register?.toLowerCase().includes(term) ||
      row.no_sertifikat?.toLowerCase().includes(term)
    );
  });

  const canDelete = (status) => status === "PENDAFTARAN" || status === "DITOLAK";

  const openHapusModal = (row) => {
    setTargetHapus(row);
    setHapusError("");
    setShowHapusModal(true);
  };

  const handleHapus = async () => {
    if (!targetHapus) return;
    try {
      setHapusLoading(true);
      setHapusError("");
      await api.delete(`/pendaftaran-penyelia/calon/${targetHapus.no_register}`);
      setShowHapusModal(false);
      setTargetHapus(null);
      fetchData();
    } catch (err) {
      setHapusError(err.response?.data?.message || "Gagal menghapus data.");
    } finally {
      setHapusLoading(false);
    }
  };

  return (
    <>
    <Container fluid>
      <div className="dashboard-header mb-4">
        <h2>
          <i className="fas fa-search me-2"></i>
          Inquiry Peserta Pelatihan
        </h2>
        <p className="text-muted mb-0">
          Semua data peserta pelatihan penyelia halal beserta status terkini
        </p>
      </div>

      {/* Filter */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={2}>
              <Form.Label className="fw-semibold mb-1">Status</Form.Label>
              <Form.Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="fw-semibold mb-1">Jenis Pelatihan</Form.Label>
              <Form.Select
                value={filterJenis}
                onChange={(e) => { setFilterJenis(e.target.value); setFilterAngkatan(""); }}
              >
                <option value="">— Semua —</option>
                {jenisList.map((j) => (
                  <option key={j} value={j}>{jenisPelatihanLabel[j] || j}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="fw-semibold mb-1">Angkatan</Form.Label>
              <Form.Select
                value={filterAngkatan}
                onChange={(e) => setFilterAngkatan(e.target.value)}
              >
                <option value="">— Semua —</option>
                {angkatanList.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="fw-semibold mb-1">Cari</Form.Label>
              <Form.Control
                type="text"
                placeholder="Nama, email, NIK, no. register, no. sertifikat..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Col>
            <Col md={2} className="text-end">
              <span className="text-muted small">{filteredData.length} data</span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {error && <Alert variant="danger" className="m-3">{error}</Alert>}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Memuat data...</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0" style={{ fontSize: "0.85rem" }}>
              <thead className="table-light">
                <tr>
                  <th className="ps-3">#</th>
                  <th>No. Register</th>
                  <th>Nama</th>
                  <th>Email</th>
                  <th>No. HP</th>
                  <th>NIK</th>
                  <th>Jenis Pelatihan</th>
                  <th>Angkatan</th>
                  <th>Tgl. Daftar</th>
                  <th>Status</th>
                  <th>Keterangan</th>
                  <th>No. Sertifikat</th>
                  <th>Tgl. Terbit</th>
                  <th className="text-center">Sertifikat</th>
                  <th className="text-center">Sertifikat TTE</th>
                  <th className="text-center">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={16} className="text-center text-muted py-5">
                      <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
                      Tidak ada data
                    </td>
                  </tr>
                ) : (
                  filteredData.map((row, idx) => (
                    <tr key={row.no_register || row.email}>
                      <td className="ps-3">{idx + 1}</td>
                      <td className="font-monospace">{row.no_register || "-"}</td>
                      <td className="fw-semibold">{row.nama || "-"}</td>
                      <td>{row.email}</td>
                      <td>{row.no_hp || "-"}</td>
                      <td>{row.nik || "-"}</td>
                      <td>{jenisPelatihanLabel[row.jenis_pelatihan] || row.jenis_pelatihan || "-"}</td>
                      <td>{row.angkatan || "-"}</td>
                      <td>{formatDate(row.tgl_daftar)}</td>
                      <td>
                        <StatusBadge status={row.status} fileSertifikat={row.file_sertifikat} />
                      </td>
                      <td style={{ maxWidth: 180, whiteSpace: "normal" }}>
                        {row.keterangan || "-"}
                      </td>
                      <td className="font-monospace" style={{ whiteSpace: "nowrap" }}>
                        {row.no_sertifikat || "-"}
                      </td>
                      <td>{formatDate(row.tgl_terbit)}</td>
                      <td className="text-center">
                        {row.file_sertifikat ? (
                          <Button
                            as="a"
                            href={`${BASE_URL}/uploads/sertifikat/${row.file_sertifikat}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                            variant="outline-success"
                            title="Unduh sertifikat"
                          >
                            <i className="fas fa-download me-1"></i>Unduh
                          </Button>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        {row.file_sertifikat_tte ? (
                          <Button
                            as="a"
                            href={`https://lembaga.halal.go.id/files/${row.file_sertifikat_tte}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                            variant="outline-primary"
                            title="Unduh sertifikat TTE"
                          >
                            <i className="fas fa-download me-1"></i>Unduh TTE
                          </Button>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        {canDelete(row.status) ? (
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => openHapusModal(row)}
                            title="Hapus data"
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>

      {/* Modal Konfirmasi Hapus */}
      <Modal show={showHapusModal} onHide={() => !hapusLoading && setShowHapusModal(false)} centered>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title><i className="fas fa-trash me-2"></i>Hapus Data Peserta</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {hapusError && <Alert variant="danger">{hapusError}</Alert>}
          <p>Apakah Anda yakin ingin <strong>menghapus permanen</strong> data berikut?</p>
          {targetHapus && (
            <table className="table table-sm table-borderless">
              <tbody>
                <tr><td className="text-muted">No. Register</td><td className="fw-semibold">{targetHapus.no_register}</td></tr>
                <tr><td className="text-muted">Nama</td><td className="fw-semibold">{targetHapus.nama}</td></tr>
                <tr><td className="text-muted">Email</td><td>{targetHapus.email}</td></tr>
                <tr><td className="text-muted">Status</td><td><StatusBadge status={targetHapus.status} fileSertifikat={targetHapus.file_sertifikat} /></td></tr>
              </tbody>
            </table>
          )}
          <Alert variant="warning" className="mb-0 py-2">
            <i className="fas fa-exclamation-triangle me-1"></i>
            Tindakan ini tidak dapat dibatalkan. Data dan akun peserta akan dihapus permanen.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHapusModal(false)} disabled={hapusLoading}>
            Batal
          </Button>
          <Button variant="danger" onClick={handleHapus} disabled={hapusLoading}>
            {hapusLoading ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="fas fa-trash me-1"></i>}
            Ya, Hapus
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PesertaInquiry;
